import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ax } from "../lib/adminApi";

export const BLAST_KINDS = {
  launch: { title: "Launch blast preview", preview: "/admin/launch-blast/preview", send: "/admin/launch-blast" },
  countdown: { title: "Countdown teaser preview", preview: "/admin/countdown-blast/preview", send: "/admin/countdown-blast" },
};

export const useAdminSession = (initialUser) => {
  const [user, setUser] = useState(initialUser || null);

  useEffect(() => {
    if (initialUser) return;
    ax.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, [initialUser]);

  const logout = async () => {
    await ax.post("/auth/logout");
    setUser(false);
  };

  return { user, setUser, logout };
};

export const useAdminData = (user, onUnauthorized) => {
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);

  const loadData = useCallback(async () => {
    const [l, a] = await Promise.all([ax.get("/admin/leads"), ax.get("/admin/applications")]);
    setLeads(l.data);
    setApps(a.data);
  }, []);

  useEffect(() => {
    if (user && user.role === "admin")
      loadData().catch((err) => {
        if (err.response?.status === 401) onUnauthorized();
      });
  }, [user, loadData, onUnauthorized]);

  return { leads, setLeads, apps, setApps, loadData };
};

export const useAdminActions = ({ loadData, setLeads, setApps }) => {
  const [busy, setBusy] = useState(null);
  const [blastKind, setBlastKind] = useState(null);
  const [blastPreview, setBlastPreview] = useState(null);
  const [blastSending, setBlastSending] = useState(false);

  const decide = async (id, status) => {
    if (busy) return;
    setBusy(id);
    try {
      const { data } = await ax.patch(`/admin/applications/${id}/status`, { status });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status: data.status } : a)));
      toast.success(`Application ${status} — decision email sent to the applicant.`);
    } catch {
      toast.error("Couldn't update the application. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const openBlast = async (kind) => {
    try {
      const { data } = await ax.get(BLAST_KINDS[kind].preview);
      setBlastPreview(data);
      setBlastKind(kind);
    } catch {
      toast.error("Couldn't load the email preview. Try again.");
    }
  };

  const confirmBlast = async () => {
    setBlastSending(true);
    try {
      const { data } = await ax.post(BLAST_KINDS[blastKind].send);
      toast.success(
        data.queued
          ? `Queued — sending to ${data.queued} waitlist member${data.queued === 1 ? "" : "s"}.`
          : "Everyone on the waitlist has already received this email."
      );
      setBlastKind(null);
      setTimeout(() => loadData().catch(() => {}), 2500);
    } catch {
      toast.error("Couldn't queue the send. Try again.");
    } finally {
      setBlastSending(false);
    }
  };

  const honourPerk = async (leadId, milestone) => {
    setBusy(`${leadId}-${milestone}`);
    try {
      await ax.patch(`/admin/leads/${leadId}/perks/${milestone}/honour`);
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId
            ? { ...l, perks: (l.perks || []).map((p) => (p.milestone === milestone ? { ...p, honoured: true } : p)) }
            : l
        )
      );
      toast.success("Perk marked as honoured.");
    } catch {
      toast.error("Couldn't update the perk. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const sendDigest = async () => {
    try {
      await ax.post("/admin/digest/send");
      toast.success("Weekly digest is on its way to your inbox.");
    } catch {
      toast.error("Couldn't send the digest. Try again.");
    }
  };

  const exportCsv = async (tab) => {
    try {
      const { data } = await ax.get(`/admin/export/${tab}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `freshly_${tab}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Export failed. Try again.");
    }
  };

  return {
    busy,
    blastKind,
    setBlastKind,
    blastPreview,
    blastSending,
    decide,
    openBlast,
    confirmBlast,
    honourPerk,
    sendDigest,
    exportCsv,
  };
};
